<!-- Inc 4: .dark va en un contenedor PADRE. La variante es `&:is(.dark *)`, así que el
     elemento que lleva .dark no recibe sus propios dark:* (el fondo quedaba claro). -->
<div [class.dark]="link?.darkMode" [style.color-scheme]="link?.darkMode ? 'dark' : null">
<div class="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 py-6 sm:py-10 px-4"
  [style.--brand-color]="link?.brandColor || '#3b82f6'">

  <div class="mx-auto" [class.max-w-4xl]="step === 1" [class.max-w-2xl]="step !== 1">
    @if (loading) {
      <div class="rounded-2xl bg-white dark:bg-gray-800 shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 py-24 text-center text-gray-500 dark:text-gray-400">
        <svg class="animate-spin h-7 w-7 mx-auto mb-3 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
        </svg>
        Cargando…
      </div>
    } @else if (error && !submitted) {
      <div class="rounded-2xl bg-white dark:bg-gray-800 shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 p-10 text-center">
        <p class="text-lg font-medium text-error-600">{{ error }}</p>
      </div>
    } @else if (link) {
      <div class="rounded-2xl bg-white dark:bg-gray-800 shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">

        <!-- ═══════════════ Step 1: Calendly de 2 columnas ═══════════════ -->
        @if (step === 1) {
          <div class="md:grid md:grid-cols-[300px_1fr]">

            <!-- ── Panel izquierdo: branding + info ── -->
            <aside class="p-6 sm:p-8 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40">
              @if (link.logoUrl) {
                <img [src]="link.logoUrl" alt="Logo" class="h-11 mb-5 object-contain">
              }
              <h1 class="text-xl font-bold text-gray-900 dark:text-white leading-snug">{{ link.title || 'Agendar reunión' }}</h1>

              <div class="mt-4 space-y-2.5 text-sm text-gray-600 dark:text-gray-400">
                @if (selectedDuration) {
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    <span>{{ selectedDuration.durationMinutes }} min</span>
                  </div>
                }
                @if (link.timezone) {
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    <span>{{ link.timezone }}</span>
                  </div>
                }
                @if (channelLabel) {
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.55-2.28A1 1 0 0121 8.62v6.76a1 1 0 01-1.45.9L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"/></svg>
                    <span class="font-medium text-gray-700 dark:text-gray-300">{{ channelLabel }}</span>
                  </div>
                }
                @if (link.requiresPayment && link.paymentAmount) {
                  <div class="inline-flex items-center gap-1.5 rounded-full bg-warning-100 dark:bg-warning-500/15 px-2.5 py-1 text-xs font-medium text-warning-700 dark:text-warning-400">
                    {{ link.paymentAmount }} {{ link.paymentCurrency }}
                  </div>
                }
              </div>

              @if (link.description) {
                <p class="mt-5 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">{{ link.description }}</p>
              }

              @if (browserTimezone && browserTimezone !== link.timezone) {
                <p class="mt-6 text-xs text-gray-400">🌍 Los horarios se muestran en tu zona: <span class="font-medium text-gray-500 dark:text-gray-300">{{ browserTimezone }}</span></p>
              }
            </aside>

            <!-- ── Panel derecho: calendario + slots ── -->
            <section class="p-6 sm:p-8">
              <!-- Header del calendario -->
              <div class="flex items-center justify-between mb-4">
                <h2 class="text-base font-semibold text-gray-900 dark:text-white capitalize">{{ calendarMonthLabel }}</h2>
                <div class="flex items-center gap-1">
                  <button type="button" (click)="prevMonth()" [disabled]="!canGoPrevMonth"
                    aria-label="Mes anterior"
                    class="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-30 disabled:hover:bg-transparent">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
                  </button>
                  <button type="button" (click)="nextMonth()" [disabled]="!canGoNextMonth"
                    aria-label="Mes siguiente"
                    class="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-30 disabled:hover:bg-transparent">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                  </button>
                </div>
              </div>

              <!-- Encabezado de días de la semana -->
              <div class="grid grid-cols-7 mb-1">
                @for (w of weekdayLabels; track $index) {
                  <div class="text-center text-xs font-medium text-gray-400 py-1">{{ w }}</div>
                }
              </div>

              <!-- Cuadrícula de días -->
              <div class="space-y-1">
                @for (week of calendarWeeks; track $index) {
                  <div class="grid grid-cols-7 gap-1">
                    @for (cell of week; track cell.date) {
                      @if (cell.selectable) {
                        <button type="button" (click)="selectDate(cell.date)"
                          class="relative aspect-square flex items-center justify-center rounded-full text-sm font-medium transition-colors"
                          [style.backgroundColor]="cell.isSelected ? 'var(--brand-color)' : ''"
                          [ngClass]="cell.isSelected
                            ? 'text-white'
                            : 'text-gray-900 bg-brand-50 hover:bg-brand-100 dark:text-white dark:bg-brand-500/25 dark:hover:bg-brand-500/40'"
                          [class.ring-1]="cell.isToday && !cell.isSelected"
                          [class.ring-brand-400]="cell.isToday && !cell.isSelected">
                          {{ cell.day }}
                          @if (!cell.isSelected) {
                            <span class="absolute bottom-1 w-1 h-1 rounded-full" [style.backgroundColor]="'var(--brand-color)'"></span>
                          }
                        </button>
                      } @else {
                        <div class="aspect-square flex items-center justify-center text-sm"
                          [ngClass]="cell.inMonth ? 'text-gray-300 dark:text-gray-300' : 'text-gray-200 dark:text-gray-400'"
                          [class.line-through]="cell.inMonth && isDateBlocked(cell.date)">
                          {{ cell.day }}
                        </div>
                      }
                    }
                  </div>
                }
              </div>

              <!-- Slots del día elegido -->
              @if (selectedDate) {
                <div class="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <p class="text-sm font-semibold text-gray-900 dark:text-white capitalize mb-3">{{ selectedDateLabel }}</p>
                  @if (selectedDateHoliday) {
                    <div class="flex items-center gap-2 rounded-lg bg-gray-100 dark:bg-gray-700/50 p-3 text-sm text-gray-600 dark:text-gray-300">
                      <span>🚫</span>
                      <span>Este día no está disponible: <strong>{{ selectedDateHoliday.name }}</strong>. Elige otra fecha.</span>
                    </div>
                  } @else if (selectedDateOverride) {
                    <div class="flex items-center gap-2 rounded-lg bg-gray-100 dark:bg-gray-700/50 p-3 text-sm text-gray-600 dark:text-gray-300">
                      <span>🚫</span>
                      <span>Este día no está disponible@if (selectedDateOverride.reason) { : <strong>{{ selectedDateOverride.reason }}</strong> }. Elige otra fecha.</span>
                    </div>
                  } @else if (slotsForSelectedDate.length === 0) {
                    <p class="text-sm text-gray-500 dark:text-gray-400">No hay horarios disponibles este día.</p>
                  } @else {
                    <div class="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
                      @for (s of slotsForSelectedDate; track s) {
                        <button type="button" (click)="selectSlot(s)"
                          class="py-2.5 text-sm font-medium rounded-lg border transition-colors"
                          [style.backgroundColor]="selectedSlot === s ? 'var(--brand-color)' : ''"
                          [style.borderColor]="selectedSlot === s ? 'var(--brand-color)' : ''"
                          [class.text-white]="selectedSlot === s"
                          [ngClass]="selectedSlot === s
                            ? ''
                            : 'border-gray-300 text-gray-700 hover:border-brand-400 dark:border-gray-500 dark:text-gray-100 dark:hover:border-brand-400'">
                          {{ s }}
                        </button>
                      }
                    </div>
                  }
                </div>
              } @else {
                <p class="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-400 text-center">
                  Selecciona un día con disponibilidad para ver los horarios.
                </p>
              }

              <div class="flex justify-end mt-6">
                <button type="button" (click)="goToForm()"
                  [disabled]="!selectedDate || !selectedSlot || !selectedDuration"
                  [style.backgroundColor]="'var(--brand-color)'"
                  class="px-6 py-2.5 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity disabled:opacity-40">
                  Continuar
                </button>
              </div>
            </section>
          </div>
        }

        <!-- ═══════════════ Step 2 & 3: header + cuerpo ═══════════════ -->
        @if (step !== 1) {
          <div class="p-6 border-b border-gray-200 dark:border-gray-700">
            @if (link.logoUrl) {
              <img [src]="link.logoUrl" alt="Logo" class="h-10 mb-3 object-contain">
            }
            <h1 class="text-xl font-bold text-gray-900 dark:text-white">{{ link.title || 'Agendar reunión' }}</h1>
          </div>
        }

        <!-- ═══ Step 2: Form ═══ -->
        @if (step === 2) {
          <div class="p-6 space-y-5">
            <div class="rounded-lg bg-brand-50 dark:bg-brand-900/20 p-4 flex items-start gap-3">
              <svg class="w-5 h-5 shrink-0 mt-0.5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
              <div>
                <p class="text-sm font-medium text-brand-700 dark:text-brand-400 capitalize">{{ formattedSelected() }}</p>
                <p class="text-xs text-brand-600 dark:text-brand-500 mt-0.5">{{ selectedDuration?.durationMinutes }} minutos</p>
              </div>
            </div>

            <!-- Datos del invitado (siempre presentes) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Nombre <span class="text-error-500">*</span>
                </label>
                <input type="text" [(ngModel)]="inviteeName" placeholder="Tu nombre"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Apellido
                </label>
                <input type="text" [(ngModel)]="inviteeLastName" placeholder="Tu apellido"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Correo electrónico <span class="text-error-500">*</span>
              </label>
              <input type="email" [(ngModel)]="inviteeEmail" placeholder="tucorreo@ejemplo.com"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                [class.border-error-500]="inviteeEmail.length > 0 && !inviteeEmailValid">
              @if (inviteeEmail.length > 0 && !inviteeEmailValid) {
                <p class="mt-1 text-xs text-error-500">Ingresa un correo válido.</p>
              }
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Teléfono
              </label>
              <input type="tel" [(ngModel)]="inviteePhone" placeholder="+51 999 888 777"
                class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
            </div>

            @for (f of formFields; track f.id) {
              @if (f.fieldType !== 'hidden' && isFieldVisible(f)) {
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    {{ f.fieldLabel }}
                    @if (f.isRequired) { <span class="text-error-500">*</span> }
                  </label>

                  @switch (f.fieldType) {
                    @case ('textarea') {
                      <textarea [(ngModel)]="formValues[f.fieldName]" [placeholder]="f.placeholder || ''" rows="3"
                        class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"></textarea>
                    }
                    @case ('dropdown') {
                      <select [(ngModel)]="formValues[f.fieldName]"
                        class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
                        <option value="">— Selecciona —</option>
                        @for (opt of parseOptions(f.options); track opt) {
                          <option [value]="opt">{{ opt }}</option>
                        }
                      </select>
                    }
                    @case ('radio') {
                      <div class="space-y-1.5">
                        @for (opt of parseOptions(f.options); track opt) {
                          <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <input type="radio" [name]="f.fieldName" [value]="opt"
                              [(ngModel)]="formValues[f.fieldName]"
                              class="h-4 w-4 border-gray-300 text-brand-500">
                            {{ opt }}
                          </label>
                        }
                      </div>
                    }
                    @case ('checkbox') {
                      <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" [(ngModel)]="formValues[f.fieldName]"
                          class="h-4 w-4 rounded border-gray-300 text-brand-500">
                        {{ f.placeholder || 'Sí' }}
                      </label>
                    }
                    @case ('number') {
                      <input type="number" [(ngModel)]="formValues[f.fieldName]" [placeholder]="f.placeholder || ''"
                        class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
                    }
                    @default {
                      <input [type]="f.fieldType === 'phone' ? 'tel' : f.fieldType"
                        [(ngModel)]="formValues[f.fieldName]" [placeholder]="f.placeholder || ''"
                        class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
                    }
                  }
                </div>
              }
            }

            @if (link.gdprConsentEnabled) {
              <label class="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                <input type="checkbox" [(ngModel)]="formValues['_gdpr']" required
                  class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500">
                <span>
                  {{ link.gdprConsentText || 'Acepto el procesamiento de mis datos.' }}
                  @if (link.gdprPrivacyUrl) {
                    <a [href]="link.gdprPrivacyUrl" target="_blank" class="text-brand-600 hover:underline">Política de privacidad</a>
                  }
                </span>
              </label>
            }

            @if (error) {
              <p class="text-sm text-error-600">{{ error }}</p>
            }

            <div class="flex justify-between">
              <button type="button" (click)="backToSlots()"
                class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                ← Atrás
              </button>
              <button type="button" (click)="submit()" [disabled]="!canSubmit() || submitting"
                [style.backgroundColor]="'var(--brand-color)'"
                class="px-6 py-2.5 rounded-lg text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity disabled:opacity-40">
                {{ submitting ? 'Agendando…' : 'Confirmar reserva' }}
              </button>
            </div>
          </div>
        }

        <!-- ═══ Step 3: Confirmación ═══ -->
        @if (step === 3 && submitted) {
          <div class="p-10 text-center space-y-3">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success-100 text-success-600">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">¡Reserva confirmada!</h2>
            <!-- INC 80: `confirmedWhen` y no `formattedSelected()` — al refrescar la
                 ocupación tras reservar, el slot recién tomado deja de estar disponible y
                 el getter se quedaba vacío. -->
            <p class="text-gray-600 dark:text-gray-400 capitalize">{{ confirmedWhen }}</p>
            @if (confirmation?.title) {
              <p class="text-sm text-gray-600 dark:text-gray-400">{{ confirmation?.title }}</p>
            }
            @if (confirmedInviteeName) {
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ confirmedInviteeName }}</p>
            }
            <!-- INC 80: aquí había un botón "Unirse a la videollamada". El acceso a la reunión
                 va por el correo de confirmación o el evento del calendario, no por una página
                 pública que cualquiera con el slug puede abrir. El backend tampoco devuelve ya
                 el enlace (ver PublicBookingResponse). -->
            <p class="text-sm text-gray-500 dark:text-gray-400">Recibirás un email de confirmación en breve.</p>
          </div>
        }

        @if (!link.hideBranding) {
          <div class="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-center">
            <p class="text-xs text-gray-500 dark:text-gray-400">Powered by PARTNER CRM</p>
          </div>
        }
      </div>
    }
  </div>
</div>
</div>
